import { useCallback, useEffect, useState } from 'react';
import { execApi } from '../../../services/api';

// What a therapist wrote back on their monthly report — and what WE wrote to them.
//
// `salary_app.approvals` is append-only and holds every event — sends, approvals,
// and the automatic "the figure changed, approve again" resets. Only what a PERSON
// wrote belongs on screen; the rest is bookkeeping (Ohad, 2026-07-30). Until this
// existed, a therapist who answered "this number is wrong" filed it into a table
// nobody opened.
//
// Marking one handled hides it and keeps it: a row is written naming the comment it
// answers, so the objection and the answer both survive in the payroll trail.
// Re-sending the report is a separate act — the שלח button in the row.
//
// A NOTE goes the other way (Basel, 2026-08-04): it opens the conversation instead
// of answering one, and shows on the therapist's report page. It is not an email —
// they see it when they next open their report.

interface ThreadComment {
  // Microsecond-precise timestamp string — the comment's identity in a table with
  // no id column. Sent back verbatim when marking it handled.
  comment_id: string;
  comment: string;
  actor: string;
  acted_at: string | null;
  // Handled comments STAY on screen, marked, until the month closes — one that
  // vanished on בוצע read as deleted to Basel (Ohad, 2026-08-04).
  resolved: boolean;
  reply: string | null;
  replied_at: string | null;
}

interface StaffNote { at: string; note: string; actor: string }

const when = (ts: string | null) => (ts ? ts.slice(0, 16).replace('T', ' ') : '');

export function ApprovalThread({ month, therapist, count, onResolved }: {
  month: string; therapist: string; count: number; onResolved: () => void;
}) {
  const [comments, setComments] = useState<ThreadComment[] | null>(null);
  const [notes, setNotes] = useState<StaffNote[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [reply, setReply] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [sendingNote, setSendingNote] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await execApi.getSalaryComments(month, therapist);
      setComments(res.data.comments as ThreadComment[]);
      setNotes((res.data.notes as StaffNote[]) || []);
      setPicked([]);
    } catch {
      setError('לא ניתן לטעון את ההערות');
    }
  }, [month, therapist]);

  useEffect(() => { load(); }, [load]);

  const toggle = (ts: string) =>
    setPicked((p) => (p.includes(ts) ? p.filter((x) => x !== ts) : [...p, ts]));

  const resolve = async () => {
    setSaving(true);
    setError(null);
    setSent(null);
    try {
      const hadReply = reply.trim().length > 0;
      const res = await execApi.resolveSalaryComments(month, therapist, picked, reply.trim() || undefined);
      setReply('');
      // Whether the answer actually left the building. Saying only 'saved' is how
      // Basel ended up resending whole reports to deliver a sentence — if the mail
      // did not go, he has to know here and not from the therapist next week.
      if (hadReply) {
        setSent(res.data?.notified
          ? `התשובה נשלחה במייל ל-${res.data.notified}`
          : 'התשובה נשמרה — לא נשלח מייל (אין כתובת או שהשליחה כבויה)');
      }
      await load();
      onResolved();                 // the row's badge is counted server-side
    } catch {
      setError('השמירה נכשלה');
    } finally {
      setSaving(false);
    }
  };

  const sendNote = async () => {
    setSendingNote(true);
    setError(null);
    try {
      await execApi.addSalaryNote(month, therapist, note.trim());
      setNote('');
      await load();
    } catch {
      setError('שמירת ההערה נכשלה');
    } finally {
      setSendingNote(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-medium text-gray-500">הערות מהמטפל/ת</div>
        {count > 0 && (
          <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5">
            {count} פתוחות
          </span>
        )}
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto max-h-64">
        {comments === null && <div className="text-xs text-gray-500">טוען…</div>}
        {comments?.length === 0 && notes.length === 0 && (
          <div className="text-xs text-gray-500">אין הערות החודש.</div>
        )}
        {/* Our notes to them, in blue — visible so nobody writes the same note twice. */}
        {notes.map((n, i) => (
          <div key={`n${i}`} className="rounded-lg px-3 py-2 border bg-blue-50 border-blue-200">
            <span className="block text-[11px] text-blue-700 font-medium">הערה שלנו · {n.at}</span>
            <span className="block text-xs text-blue-900 mt-0.5 whitespace-pre-wrap leading-relaxed">
              {n.note}
            </span>
          </div>
        ))}
        {comments?.map((c) => {
          const ts = c.comment_id;
          const on = picked.includes(ts);
          // Handled — stays on screen, marked, until the month closes.
          if (c.resolved) {
            return (
              <div key={ts} className="rounded-lg px-3 py-2 border bg-gray-50 border-gray-200">
                <span className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-gray-500" dir="ltr">{when(c.acted_at)}</span>
                  <span className="text-[11px] font-medium text-green-700 whitespace-nowrap"
                        title={c.replied_at ? `טופל ב-${c.replied_at}` : ''}>
                    ✓ בוצע
                  </span>
                </span>
                <span className="block text-xs text-gray-500 mt-0.5 whitespace-pre-wrap leading-relaxed">
                  {c.comment}
                </span>
                {c.reply && (
                  <span className="block text-[11px] text-gray-500 mt-1 border-t border-gray-200 pt-1 whitespace-pre-wrap">
                    ↩ {c.reply}
                  </span>
                )}
              </div>
            );
          }
          return (
            <label key={ts}
              className={`flex gap-2 items-start rounded-lg px-3 py-2 cursor-pointer border ${
                on ? 'bg-blue-50 border-blue-300' : 'bg-amber-50 border-amber-200'}`}>
              <input type="checkbox" checked={on} onChange={() => toggle(ts)}
                     className="mt-0.5 accent-blue-600" />
              <span className="min-w-0">
                <span className="block text-[11px] text-gray-500" dir="ltr">{when(c.acted_at)}</span>
                <span className="block text-xs text-gray-800 mt-0.5 whitespace-pre-wrap leading-relaxed">
                  {c.comment}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      {/* A written answer is optional: often the fix IS the answer, and demanding
          text only teaches people to type a dot. */}
      <div className="mt-3 border-t border-gray-200 pt-3">
        <textarea
          value={reply}
          onChange={(ev) => setReply(ev.target.value)}
          rows={2}
          placeholder="תשובה (לא חובה) — מה תוקן"
          className="w-full text-xs border border-gray-300 rounded-lg p-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
        {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
        {sent && (
          <div className={`text-xs mt-1 ${sent.startsWith('התשובה נשלחה') ? 'text-green-700' : 'text-amber-700'}`}>
            {sent}
          </div>
        )}
        <div className="flex items-center justify-between mt-2 gap-2">
          <button
            onClick={resolve}
            disabled={saving || picked.length === 0}
            title={picked.length ? '' : 'סמן/י הערה כדי לסגור אותה'}
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400"
          >
            {saving ? 'שומר…' : `בוצע${picked.length > 1 ? ` (${picked.length})` : ''}`}
          </button>
          <span className="text-[11px] text-gray-500">שליחה מחדש — בכפתור "שלח שוב"</span>
        </div>
      </div>

      {/* A note of our own — not tied to any comment; starts the thread. */}
      <div className="mt-3 border-t border-gray-200 pt-3">
        <div className="text-[11px] font-medium text-gray-500 mb-1">✎ הערה יזומה למטפל/ת</div>
        <textarea
          value={note}
          onChange={(ev) => setNote(ev.target.value)}
          rows={2}
          placeholder="למשל: עדכנו את שעות הישיבה — נא לבדוק"
          className="w-full text-xs border border-gray-300 rounded-lg p-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
        <div className="flex items-center justify-between mt-2 gap-2">
          <button
            onClick={sendNote}
            disabled={sendingNote || !note.trim()}
            className="rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:border-gray-200 disabled:text-gray-300"
          >
            {sendingNote ? 'שומר…' : 'הוסף הערה'}
          </button>
          <span className="text-[11px] text-gray-500">תופיע בדוח של המטפל/ת</span>
        </div>
      </div>
    </div>
  );
}
